"use client"

import { useState, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import { useLanguage } from "@/context/language-context"

interface ModernDatePickerProps {
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  autoOpen?: boolean
  portalContainer?: HTMLElement | null
  highZIndex?: boolean
  forcePointerEvents?: boolean
  minDate?: string | null
  maxDate?: string | null
  /**
   * When true the picker renders a 24h HH:MM time row beside the calendar and
   * the emitted value is a full ISO 8601 UTC string (e.g. `2026-06-10T19:30:00.000Z`).
   * Time is interpreted in the user's local timezone on the way in and converted
   * to UTC on the way out — display sites (formatDateTime) convert it back to
   * local for rendering, so the round-trip is invisible to the user.
   *
   * When false (default) the picker behaves exactly as before: it reads/writes
   * date-only `YYYY-MM-DD` strings and ignores the time portion of incoming ISO
   * values. Existing callers don't change.
   */
  showTime?: boolean
}

const MONTHS = {
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ],
  fr: [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ]
}

const DAYS = {
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  fr: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"]
}

// Parse YYYY-MM-DD (or ISO with time) as a local-midnight Date so the picker
// reflects the correct calendar day in the user's timezone.
function parseLocalDate(dateString: string): Date {
  if (!dateString) return new Date()
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number)
  return new Date(year, month - 1, day)
}

// Parse a full ISO 8601 datetime string and return a Date in the user's local
// timezone. The native `new Date(iso)` already does the UTC→local conversion;
// this wrapper just normalizes null / bad input to a fresh Date. We use this
// only when showTime=true — date-only callers stay on parseLocalDate so the
// picker continues to pin to local-midnight regardless of any time portion.
function parseLocalDateTime(dateString: string): Date {
  if (!dateString) return new Date()
  const parsed = new Date(dateString)
  if (isNaN(parsed.getTime())) return new Date()
  return parsed
}

function getToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

// Pad to two digits. Used for HH:MM rendering in the time row.
function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function ModernDatePicker({ value, onChange, placeholder = "selectDate", disabled = false, autoOpen = false, portalContainer = null, highZIndex = false, forcePointerEvents = false, minDate = null, maxDate = null, showTime = false }: ModernDatePickerProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(autoOpen)
  const [viewMode, setViewMode] = useState<'days' | 'months' | 'years'>('days')
  const [currentDate, setCurrentDate] = useState(new Date())

  // Working time-of-day for the showTime variant. Kept ALWAYS in 24h
  // local-time so the Date constructor in handleDateSelect / emitTimeChange
  // gets unambiguous input; the 12h vs 24h decision lives in the render layer
  // and the input parsers. Seeded from the incoming value when one exists;
  // otherwise defaults to now-local so brand-new picks land at a sensible
  // "right now" timestamp rather than midnight (saves the user from
  // rebuilding the time after picking a date).
  const seededTime = useMemo(() => {
    if (!showTime) return { hours: 0, minutes: 0 }
    if (value) {
      const d = parseLocalDateTime(value)
      return { hours: d.getHours(), minutes: d.getMinutes() }
    }
    const now = new Date()
    return { hours: now.getHours(), minutes: now.getMinutes() }
  }, [showTime, value])
  const [timeHours, setTimeHours] = useState<number>(seededTime.hours)
  const [timeMinutes, setTimeMinutes] = useState<number>(seededTime.minutes)

  // Locale-driven 12/24h selector. English uses 12h with AM/PM (matches
  // formatDateTime's en-US render: "06-09-2026 06:27 PM"). French uses 24h
  // with no meridiem (matches the audit panel's fr-FR render at task #195).
  // Keep internal state in 24h regardless — only the input/label layer flips.
  const isFrench = language === 'fr'
  const use12h = !isFrench

  // 1-12 hour display for 12h mode; 0-23 for 24h. Two helpers keep the input
  // value and the parser symmetric so we don't accidentally interpret an
  // input value as the wrong format.
  const to12hLabel = (h24: number): number => {
    const mod = h24 % 12
    return mod === 0 ? 12 : mod
  }
  const isPm = timeHours >= 12

  // Re-seed time when the picker opens with a fresh value — otherwise the
  // first-render seed sticks and a re-open against a different row would show
  // the previous row's time. Only the popover transitions matter; the user's
  // own edits to the time inputs stay intact within a single open session.
  useEffect(() => {
    if (!showTime) return
    if (isOpen) {
      setTimeHours(seededTime.hours)
      setTimeMinutes(seededTime.minutes)
    }
  }, [isOpen, showTime, seededTime])

  const minDateParsed = useMemo(() => minDate ? parseLocalDate(minDate) : null, [minDate])
  const maxDateParsed = useMemo(() => maxDate ? parseLocalDate(maxDate) : null, [maxDate])

  const minY = minDateParsed?.getFullYear() ?? null
  const minM = minDateParsed?.getMonth() ?? null
  const maxY = maxDateParsed?.getFullYear() ?? null
  const maxM = maxDateParsed?.getMonth() ?? null

  const today = getToday()
  // For showTime mode we need the picker to land on the same calendar day
  // shown in the user's local timezone — `new Date(iso).getFullYear()` etc.
  // already reflect local time. For date-only mode we keep the legacy
  // local-midnight parser to avoid timezone slippage on bare YYYY-MM-DD
  // values that have no time portion.
  const selectedDate = value
    ? (showTime ? parseLocalDateTime(value) : parseLocalDate(value))
    : null

  // Initialize current date to selected date, or the nearest month with selectable dates
  useEffect(() => {
    if (value) {
      setCurrentDate(showTime ? parseLocalDateTime(value) : parseLocalDate(value))
    } else if (minDateParsed && minDateParsed.getTime() > getToday().getTime()) {
      setCurrentDate(minDateParsed)
    } else if (maxDateParsed && maxDateParsed.getTime() < getToday().getTime()) {
      setCurrentDate(maxDateParsed)
    }
  }, [value, minDateParsed, maxDateParsed, showTime])

  useEffect(() => {
    if (autoOpen) {
      setIsOpen(true)
    }
  }, [autoOpen])

  const handleDateSelect = (date: Date) => {
    if (!date) {
      onChange(null)
      setIsOpen(false)
      setViewMode('days')
      return
    }

    if (showTime) {
      // Merge the picked calendar day with the current time inputs (interpreted
      // as the user's LOCAL time), then emit as ISO 8601 UTC. The native Date
      // constructor in local-time form (Y, M, D, H, M) does the local→UTC
      // conversion implicitly when toISOString() is called. Display sites
      // (formatDateTime) reverse it. Keep the popover open so the user can
      // adjust the time after clicking a day — the explicit "Done" button
      // (handleDoneClick) is the dismissal control in showTime mode.
      const local = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        timeHours,
        timeMinutes,
        0,
        0,
      )
      onChange(local.toISOString())
      // Re-anchor the calendar to the picked day in case the user keeps the
      // popover open and changes the time afterward.
      setCurrentDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
      setViewMode('days')
      return
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    onChange(`${year}-${month}-${day}`)
    setIsOpen(false)
    setViewMode('days')
  }

  // Re-emit the current selection with updated hours/minutes. Called from the
  // HH/MM inputs in showTime mode. If no calendar day has been picked yet we
  // default to today so the user can pick a time first then a day; emits the
  // combined value either way.
  const emitTimeChange = (hours: number, minutes: number) => {
    if (!showTime) return
    const dayBasis = selectedDate || today
    const local = new Date(
      dayBasis.getFullYear(),
      dayBasis.getMonth(),
      dayBasis.getDate(),
      hours,
      minutes,
      0,
      0,
    )
    onChange(local.toISOString())
  }

  const handleHoursChange = (raw: string) => {
    // Allow empty input transiently so the user can blank the field and retype
    // without the value snapping to 0 mid-edit; only commit a numeric value.
    if (raw === '') {
      // In 12h mode the field's natural empty state is "12" (rendered as 12 AM
      // for h24=0), so we keep timeHours at its current AM/PM lobe and let the
      // user type. Internal state stays at 0 to align with the placeholder.
      setTimeHours(use12h ? (isPm ? 12 : 0) : 0)
      return
    }
    const parsed = Number(raw)
    if (Number.isNaN(parsed)) return

    let h24: number
    if (use12h) {
      // 1-12 input → combine with current meridiem to get 0-23. We accept the
      // user typing 0 transiently (some keyboards make it easy to overshoot)
      // and clamp it to 12 since that's the 12h equivalent of midnight/noon
      // for the current AM/PM lobe.
      const h12 = Math.max(1, Math.min(12, parsed === 0 ? 12 : parsed))
      h24 = (h12 % 12) + (isPm ? 12 : 0)
    } else {
      h24 = Math.max(0, Math.min(23, parsed))
    }
    setTimeHours(h24)
    emitTimeChange(h24, timeMinutes)
  }

  // AM/PM toggle for 12h mode. Flips the 12-hour offset on the underlying
  // 24h state — the calendar-day portion of the emitted ISO stays put, only
  // the hour shifts (e.g. 1 AM → 1 PM is 1 → 13). No-op in 24h mode.
  const handleMeridiemToggle = (nextIsPm: boolean) => {
    if (!use12h) return
    if (nextIsPm === isPm) return
    const h12 = to12hLabel(timeHours)
    const h24 = (h12 % 12) + (nextIsPm ? 12 : 0)
    setTimeHours(h24)
    emitTimeChange(h24, timeMinutes)
  }

  const handleMinutesChange = (raw: string) => {
    if (raw === '') {
      setTimeMinutes(0)
      return
    }
    const n = Math.max(0, Math.min(59, Number(raw)))
    if (Number.isNaN(n)) return
    setTimeMinutes(n)
    emitTimeChange(timeHours, n)
  }

  // Explicit dismiss control for showTime mode — picking a date there leaves
  // the popover open so the user can tweak HH:MM afterward, so we need a
  // visible Done button rather than relying on the auto-close behavior of
  // the date-only path.
  const handleDoneClick = () => {
    setIsOpen(false)
    setViewMode('days')
  }

  const handleClear = () => {
    onChange(null)
    setIsOpen(false)
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null)
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day))
    return days
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + (direction === 'prev' ? -1 : 1), 1))
  }

  const navigateYear = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => new Date(prev.getFullYear() + (direction === 'prev' ? -1 : 1), prev.getMonth(), 1))
  }

  const selectMonth = (monthIndex: number) => {
    setCurrentDate(prev => new Date(prev.getFullYear(), monthIndex, 1))
    setViewMode('days')
  }

  const selectYear = (year: number) => {
    setCurrentDate(prev => new Date(year, prev.getMonth(), 1))
    setViewMode('months')
  }

  const getYearRange = () => {
    const startYear = Math.floor(currentDate.getFullYear() / 10) * 10
    return Array.from({ length: 12 }, (_, i) => startYear + i)
  }

  const isBeforeMin = (date: Date) => !!minDateParsed && date.getTime() < minDateParsed.getTime()
  const isAfterMax  = (date: Date) => !!maxDateParsed && date.getTime() > maxDateParsed.getTime()
  const isToday     = (date: Date) => date.getTime() === today.getTime()
  // Date-portion-only comparison so showTime mode (where selectedDate carries
  // hours/minutes) still highlights the picked day on the calendar grid. The
  // grid cells are constructed via `new Date(y, m, d)` at local-midnight, so
  // we normalize selectedDate the same way before comparing.
  const isSelected  = (date: Date) => {
    if (!selectedDate) return false
    return (
      date.getFullYear() === selectedDate.getFullYear()
      && date.getMonth() === selectedDate.getMonth()
      && date.getDate() === selectedDate.getDate()
    )
  }

  // Returns true if the entire previous month falls before minDate
  const isPrevMonthDisabled = () => {
    if (minY === null || minM === null) return false
    const cy = currentDate.getFullYear()
    const cm = currentDate.getMonth()
    return cy < minY || (cy === minY && cm <= minM)
  }

  // Returns true if the entire next month falls after maxDate
  const isNextMonthDisabled = () => {
    if (maxY === null || maxM === null) return false
    const cy = currentDate.getFullYear()
    const cm = currentDate.getMonth()
    return cy > maxY || (cy === maxY && cm >= maxM)
  }

  // Trigger label. In date-only mode this is the localized day-month-year.
  // In showTime mode we additionally render the time portion using the SAME
  // locale rules as the grid renderer (formatDateTime in format-helpers.ts):
  // en-US → 12h with AM/PM, fr-FR → 24h with no meridiem. We compute the time
  // portion via toLocaleTimeString here rather than calling formatDateTime
  // because the date portion uses a different layout in the trigger (e.g.
  // "09 June 2026" rather than "06-09-2026").
  const formattedDate = (() => {
    if (!selectedDate) return placeholder
    const datePart = `${pad2(selectedDate.getDate())} ${MONTHS[language][selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
    if (!showTime) return datePart
    const timePart = selectedDate.toLocaleTimeString(isFrench ? 'fr-FR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !isFrench,
    })
    return `${datePart} ${timePart}`
  })()

  const renderDaysView = () => {
    const days = getDaysInMonth(currentDate)
    const todayDisabled = isBeforeMin(today) || isAfterMax(today)

    return (
      <div className="p-3">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth('prev')}
            disabled={isPrevMonthDisabled()}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => setViewMode('months')}
              className="text-sm font-medium hover:bg-gray-100"
            >
              {MONTHS[language][currentDate.getMonth()]}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setViewMode('years')}
              className="text-sm font-medium hover:bg-gray-100"
            >
              {currentDate.getFullYear()}
            </Button>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth('next')}
            disabled={isNextMonthDisabled()}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-7 mb-2">
          {DAYS[language].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((date, index) => (
            <div key={index} className="aspect-square">
              {date && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDateSelect(date)}
                  disabled={isBeforeMin(date) || isAfterMax(date)}
                  className={`w-full h-full text-sm ${
                    isSelected(date)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : isToday(date)
                      ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {date.getDate()}
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Time row — only when this picker is operating in DateTime mode.
            Two number inputs (HH + MM) for explicit entry plus an AM/PM
            toggle in 12h mode. We DON'T use <input type="time"> because it
            varies in look across browsers and on some Chrome builds swallows
            focus events fired from the surrounding Popover, which made the
            field unfocusable inside the date picker. Plain number inputs are
            predictable. Locale gates 12h vs 24h here to match the grid
            renderer (formatDateTime in utils/format-helpers.ts), so what the
            user picks reads back the same way it'll display on the row. */}
        {showTime && (
          <div className="mt-4 pt-3 border-t flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">
              {t('time' as any) || 'Time'}
            </span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={use12h ? 1 : 0}
                max={use12h ? 12 : 23}
                value={use12h ? pad2(to12hLabel(timeHours)) : pad2(timeHours)}
                onChange={(e) => handleHoursChange(e.target.value)}
                className="w-12 h-8 px-1 text-sm text-center border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                aria-label={t('hours' as any) || 'Hours'}
              />
              <span className="text-sm text-gray-500">:</span>
              <input
                type="number"
                min={0}
                max={59}
                value={pad2(timeMinutes)}
                onChange={(e) => handleMinutesChange(e.target.value)}
                className="w-12 h-8 px-1 text-sm text-center border border-gray-200 rounded focus:outline-none focus:border-blue-400"
                aria-label={t('minutes' as any) || 'Minutes'}
              />
              {use12h && (
                // Two segmented buttons rather than a single toggle so the
                // current lobe is always visually obvious — keyboard users
                // can also tab onto each independently. Click is the only
                // affordance because there's no third state.
                <div className="ml-2 inline-flex rounded border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => handleMeridiemToggle(false)}
                    className={`px-2 h-8 text-xs font-medium transition-colors ${
                      !isPm
                        ? 'bg-[#0F214B] text-white'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    aria-pressed={!isPm}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMeridiemToggle(true)}
                    className={`px-2 h-8 text-xs font-medium transition-colors ${
                      isPm
                        ? 'bg-[#0F214B] text-white'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    aria-pressed={isPm}
                  >
                    PM
                  </button>
                </div>
              )}
            </div>
            <span className="text-[11px] text-gray-400 ml-auto">
              {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </span>
          </div>
        )}

        <div className="flex justify-between mt-4 pt-3 border-t">
          <Button variant="ghost" size="sm" onClick={handleClear}>
            {t('clearAll')}
          </Button>
          {showTime ? (
            // showTime keeps the popover open after a date click (so the user
            // can refine HH:MM after picking a day); a Done button is the
            // explicit dismiss for that mode. Date-only mode auto-closes on
            // day click so the equivalent affordance is "Today" — kept as-is
            // below for that path.
            <Button variant="ghost" size="sm" onClick={handleDoneClick}>
              {t('done' as any) || 'Done'}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => handleDateSelect(today)} disabled={todayDisabled}>
              {t('today')}
            </Button>
          )}
        </div>
      </div>
    )
  }

  const renderMonthsView = () => {
    const cy = currentDate.getFullYear()

    const isMonthDisabled = (index: number) => {
      if (minY !== null && minM !== null && (cy < minY || (cy === minY && index < minM))) return true
      if (maxY !== null && maxM !== null && (cy > maxY || (cy === maxY && index > maxM))) return true
      return false
    }

    return (
      <div className="p-3">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateYear('prev')}
            disabled={minY !== null && cy - 1 < minY}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            onClick={() => setViewMode('years')}
            className="text-sm font-medium hover:bg-gray-100"
          >
            {cy}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateYear('next')}
            disabled={maxY !== null && cy + 1 > maxY}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {MONTHS[language].map((month, index) => (
            <Button
              key={month}
              variant="ghost"
              onClick={() => selectMonth(index)}
              disabled={isMonthDisabled(index)}
              className={`text-sm py-3 ${
                currentDate.getMonth() === index
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              {month.slice(0, 3)}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  const renderYearsView = () => {
    const years = getYearRange()

    return (
      <div className="p-3">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCurrentDate(prev => {
                const newDate = new Date(prev)
                newDate.setFullYear(newDate.getFullYear() - 12)
                return newDate
              })
            }}
            disabled={minY !== null && years[0] <= minY}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <span className="text-sm font-medium">
            {years[0]} - {years[years.length - 1]}
          </span>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCurrentDate(prev => {
                const newDate = new Date(prev)
                newDate.setFullYear(newDate.getFullYear() + 12)
                return newDate
              })
            }}
            disabled={maxY !== null && years[years.length - 1] >= maxY}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {years.map(year => (
            <Button
              key={year}
              variant="ghost"
              onClick={() => selectYear(year)}
              disabled={(minY !== null && year < minY) || (maxY !== null && year > maxY)}
              className={`text-sm py-3 ${
                currentDate.getFullYear() === year
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              {year}
            </Button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={`w-full justify-start text-left font-normal ${
            !selectedDate ? "text-muted-foreground" : ""
          } border border-input`}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? formattedDate : <span className="text-gray-400">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" portalContainer={portalContainer} highZIndex={highZIndex} forcePointerEvents={forcePointerEvents}>
        {viewMode === 'days' && renderDaysView()}
        {viewMode === 'months' && renderMonthsView()}
        {viewMode === 'years' && renderYearsView()}
      </PopoverContent>
    </Popover>
  )
}
